import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { money } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { StatusBadge } from "@/components/StatusBadge";
import FullScreenLoader from "@/components/FullScreenLoader";
import { DollarSign, TrendingUp, Calendar, ShoppingCart } from "lucide-react";

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <Card className="p-5 rounded-xl border-border" data-testid={`report-${label.toLowerCase().replace(/ /g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground font-medium">{label}</span>
        <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${accent}`}><Icon className="h-4 w-4" /></div>
      </div>
      <p className="mt-3 text-2xl font-bold font-heading tracking-tight">{value}</p>
    </Card>
  );
}

export default function Reports() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/store/reports").then((r) => setD(r.data)); }, []);
  if (!d) return <FullScreenLoader />;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Reports</h1>
        <p className="text-muted-foreground mt-1">A simple snapshot of your business.</p>
      </div>

      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <Stat icon={DollarSign} label="Sales Today" value={money(d.sales_today)} accent="bg-emerald-100 text-emerald-600" />
        <Stat icon={Calendar} label="Sales This Week" value={money(d.sales_week)} accent="bg-indigo-100 text-indigo-600" />
        <Stat icon={TrendingUp} label="Sales This Month" value={money(d.sales_month)} accent="bg-violet-100 text-violet-600" />
        <Stat icon={ShoppingCart} label="Avg Order Value" value={money(d.avg_order_value)} accent="bg-cyan-100 text-cyan-600" />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="rounded-xl border-border">
          <div className="p-5 border-b border-border"><h3 className="font-semibold">Top Products</h3></div>
          <div className="divide-y divide-border">
            {d.top_products.length === 0 && <p className="p-6 text-sm text-muted-foreground text-center">No sales yet.</p>}
            {d.top_products.map((p, i) => (
              <div key={i} className="flex items-center justify-between px-5 py-3">
                <span className="text-sm font-medium truncate">{p.name}</span>
                <span className="text-sm text-muted-foreground">{p.qty} sold · {money(p.revenue)}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="rounded-xl border-border">
          <div className="p-5 border-b border-border"><h3 className="font-semibold">Top Customers</h3></div>
          <div className="divide-y divide-border">
            {d.top_customers.length === 0 && <p className="p-6 text-sm text-muted-foreground text-center">No customers yet.</p>}
            {d.top_customers.map((c) => (
              <Link to={`/dashboard/customers/${c.id}`} key={c.id} className="flex items-center justify-between px-5 py-3 hover:bg-secondary/50 transition-colors">
                <span className="text-sm font-medium truncate">{c.name}</span>
                <span className="text-sm text-muted-foreground">{c.total_orders} orders · {money(c.lifetime_sales)}</span>
              </Link>
            ))}
          </div>
        </Card>

        <Card className="rounded-xl border-border">
          <div className="p-5 border-b border-border"><h3 className="font-semibold">Pending Orders</h3></div>
          <div className="divide-y divide-border">
            {d.pending_orders.length === 0 && <p className="p-6 text-sm text-muted-foreground text-center">Nothing pending. Nice.</p>}
            {d.pending_orders.map((o) => (
              <Link to={`/dashboard/orders/${o.id}`} key={o.id} className="flex items-center justify-between px-5 py-3 hover:bg-secondary/50 transition-colors">
                <span className="text-sm font-medium">{o.order_number} · {o.customer_name}</span>
                <div className="flex items-center gap-3"><StatusBadge status={o.status} /><span className="text-sm">{money(o.total)}</span></div>
              </Link>
            ))}
          </div>
        </Card>

        <Card className="rounded-xl border-border">
          <div className="p-5 border-b border-border"><h3 className="font-semibold">Low Inventory</h3></div>
          <div className="divide-y divide-border">
            {d.low_inventory.length === 0 && <p className="p-6 text-sm text-muted-foreground text-center">All stocked up.</p>}
            {d.low_inventory.map((p) => (
              <div key={p.id} className="flex items-center justify-between px-5 py-3">
                <span className="text-sm font-medium truncate">{p.name}</span>
                <span className={`text-sm font-semibold ${p.quantity === 0 ? "text-destructive" : "text-amber-600"}`}>{p.quantity === 0 ? "Out of stock" : `${p.quantity} left`}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
